/**
 * The one win-rate definition (docs/WinRateExplainer.md "One definition"):
 *   - players, pairs and trios: the points rate, (W×3 + D) / (G×3) × 100
 *   - head-to-head: W / (W + L) × 100, where 50% genuinely means level
 * Judge good / average / poor against the league average (get_league_points_rate,
 * ~48% with WNF's draw rate), never against 50%. Pure: no React or Supabase,
 * so shared/whatsapp and the bot can use it. useLeagueAverage() loads the figure.
 */

/** Points rate 0–100, or null with no games. */
export function pointsRate(wins: number, draws: number, games: number): number | null {
  if (!games) return null;
  return ((wins * 3 + draws) / (games * 3)) * 100;
}

/** Head-to-head share of the decided games, 0–100. All draws (or none played) is level: 50. */
export function headToHeadPct(wins: number, losses: number): number {
  const decided = wins + losses;
  return decided > 0 ? (wins / decided) * 100 : 50;
}

/** Used until (or if) the database figure can't be loaded. Matches the data at 1 Oct 2026. */
export const LEAGUE_AVERAGE_FALLBACK = 48;

export type RateBand = 'elite' | 'strong' | 'good' | 'average' | 'poor' | 'bad';

/**
 * Where a points rate sits against the league average:
 * elite ≥ +20, strong ≥ +12, good ≥ +5, average within ±5, poor ≥ −12, bad below.
 */
export function rateBand(rate: number, average: number = LEAGUE_AVERAGE_FALLBACK): RateBand {
  const d = rate - average;
  if (d >= 20) return 'elite';
  if (d >= 12) return 'strong';
  if (d >= 5) return 'good';
  if (d > -5) return 'average';
  if (d > -12) return 'poor';
  return 'bad';
}
